// Klient einladen (G3): Einmal-Link erzeugen + kopieren, bisherige Einladungen
// sehen und widerrufen. Der Token ist NUR direkt nach dem Erzeugen sichtbar —
// die Liste zeigt danach nur noch Status + Daten.

import { useState } from "react";

import {
  useCreateInvitation,
  useInvitations,
  useRevokeInvitation,
} from "@/api/hooks";
import type { InvitationStatus, Mandant } from "@/api/types";
import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";

import "./InvitationModal.css";

interface Props {
  mandant: Mandant;
  onClose: () => void;
}

const STATUS_LABEL: Record<InvitationStatus, string> = {
  pending: "offen",
  redeemed: "eingelöst",
  expired: "abgelaufen",
};

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("de-AT");
}

export function InvitationModal({ mandant, onClose }: Props) {
  const toast = useToast();
  const invitations = useInvitations(mandant.id);
  const create = useCreateInvitation(mandant.id);
  const revoke = useRevokeInvitation(mandant.id);
  const [freshLink, setFreshLink] = useState<string | null>(null);
  // Vorbelegt mit der Mandanten-Adresse — der Sachbearbeiter kann sie
  // überschreiben oder leeren (dann nur Link, kein automatischer Versand).
  const [email, setEmail] = useState(mandant.contact_email ?? "");
  // Adresse, an die tatsächlich gemailt wurde (aus mail_sent abgeleitet).
  const [sentTo, setSentTo] = useState<string | null>(null);

  const handleCreate = () => {
    create.mutate(
      { contactEmail: email },
      {
        onSuccess: (res) => {
          setFreshLink(
            `${window.location.origin}/klient/upload?token=${res.token}`
          );
          const ziel = email.trim();
          setSentTo(res.mail_sent ? ziel : null);
          if (res.mail_sent) {
            toast.success(`Einladung an ${ziel} verschickt.`);
          }
        },
        onError: (err) =>
          toast.error(`Erzeugen fehlgeschlagen: ${err.message}`),
      }
    );
  };

  const handleCopy = async () => {
    if (!freshLink) return;
    try {
      await navigator.clipboard.writeText(freshLink);
      toast.success("Link kopiert — jetzt dem Klienten schicken.");
    } catch {
      toast.error("Kopieren nicht möglich — Link bitte manuell markieren.");
    }
  };

  const handleRevoke = (id: string) => {
    if (
      !window.confirm("Diese Einladung widerrufen? Der Link wird ungültig.")
    ) {
      return;
    }
    revoke.mutate(id, {
      onSuccess: () => toast.success("Einladung widerrufen."),
      onError: (err) => toast.error(`Widerrufen fehlgeschlagen: ${err.message}`),
    });
  };

  return (
    <Modal
      title={`Klient einladen — ${mandant.name}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Schließen
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            loading={create.isPending}
          >
            {email.trim()
              ? "Einladung per E-Mail senden"
              : "+ Einladungslink erzeugen"}
          </Button>
        </>
      }
    >
      <div className="bmd-invite-compose">
        <label className="bmd-label" htmlFor="invite-email">
          E-Mail des Klienten <span className="bmd-subtle">(optional)</span>
        </label>
        <input
          id="invite-email"
          className="bmd-input"
          type="email"
          placeholder="klient@example.at"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <div className="bmd-subtle" style={{ fontSize: 12, marginTop: 4 }}>
          Angegeben → die Einladung wird automatisch zugemailt. Leer → es wird
          nur ein Link zum manuellen Verschicken erzeugt.
        </div>
      </div>

      {freshLink && (
        <div className="bmd-card bmd-invite-fresh">
          {sentTo ? (
            <div className="bmd-invite-sent">
              ✓ Einladung per E-Mail an <strong>{sentTo}</strong> verschickt.
              Der Link unten ist dieselbe Einladung — als Kopie zum Nachschicken.
            </div>
          ) : (
            <div className="bmd-label">
              Einladungslink — wird nur <strong>einmal</strong> angezeigt
            </div>
          )}
          <input
            className="bmd-input bmd-mono"
            readOnly
            value={freshLink}
            onFocus={(e) => e.currentTarget.select()}
          />
          <div style={{ marginTop: 8 }}>
            <Button variant="primary" size="sm" onClick={handleCopy}>
              Link kopieren
            </Button>
          </div>
          {!sentTo && (
            <div className="bmd-subtle" style={{ fontSize: 12, marginTop: 6 }}>
              Schick den Link dem Klienten (E-Mail, Messenger). Einmalig
              einlösbar, fest an diesen Mandanten gebunden.
            </div>
          )}
        </div>
      )}

      <h3 className="bmd-invite-list-title">Bisherige Einladungen</h3>
      {invitations.isLoading ? (
        <div className="bmd-subtle">Laden…</div>
      ) : invitations.isError ? (
        <div className="bmd-subtle">Einladungen konnten nicht geladen werden.</div>
      ) : (invitations.data?.length ?? 0) === 0 ? (
        <div className="bmd-subtle">
          Noch keine Einladungen für diesen Mandanten.
        </div>
      ) : (
        <ul className="bmd-invite-list">
          {invitations.data!.map((inv) => (
            <li key={inv.id} className="bmd-invite-item">
              <span className={`bmd-chip bmd-invite-${inv.status}`}>
                {STATUS_LABEL[inv.status]}
              </span>
              <span className="bmd-subtle" style={{ fontSize: 13 }}>
                erstellt {fmtDate(inv.created_at)}
                {inv.status === "pending" &&
                  ` · gültig bis ${fmtDate(inv.expires_at)}`}
              </span>
              {inv.status === "pending" && (
                <button
                  type="button"
                  className="bmd-invite-revoke"
                  onClick={() => handleRevoke(inv.id)}
                  disabled={revoke.isPending}
                >
                  Widerrufen
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
